import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Drawer, Group, Paper, ScrollArea, Stack, Table, Text, Textarea, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconRepeat, IconSearch } from '@tabler/icons-react';
import {
  RECURRING_EVENT_LABELS,
  TEST_OFFER_BADGE,
  formatRubles,
  type AdminSubscriptionRow,
  type RecurringStatus,
} from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { ADMIN_SUBSCRIPTIONS_KEY, adminCancelSubscription, fetchAdminSubscriptions } from './payments-api';

/**
 * Автопродления (docs/payments/RECURRING.md).
 *
 * Кто подключил, на каких условиях, когда следующее списание и как прошли
 * прежние. Отключить здесь — по письму покупателя в поддержку (оферта, п. 7.2.1):
 * с причиной, покупатель получит подтверждение.
 */

const STATUS: Record<RecurringStatus, { label: string; color: string }> = {
  active: { label: 'Действует', color: 'teal' },
  cancelled: { label: 'Отключено', color: 'gray' },
  failed: { label: 'Не прошла оплата', color: 'orange' },
};

const dateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('ru-RU') : '—');

function nextText(row: AdminSubscriptionRow): string {
  if (row.status !== 'active') return `не будет: отключено ${day(row.endedAt)}`;
  if (row.charging) return 'списание отправлено, ждём Robokassa';
  if (row.retryAt) return `повтор ${day(row.retryAt)} (попытка ${row.failedAttempts + 1})`;
  return day(row.nextChargeAt);
}

export function AdminSubscriptionsPage() {
  const query = useQuery({ queryKey: ADMIN_SUBSCRIPTIONS_KEY, queryFn: fetchAdminSubscriptions });
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const needle = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      (query.data?.items ?? []).filter(
        (row) => !needle || [row.user?.email, row.title, row.motherInvoiceId].some((value) => value?.toLowerCase().includes(needle)),
      ),
    [query.data, needle],
  );
  const open = query.data?.items.find((row) => row.id === openId) ?? null;

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Автопродления"
        description="Подключённые автопродления: условия, следующее списание и журнал. Отключить — по письму покупателя."
      />
      <TextInput
        size="xs"
        w={300}
        mb="md"
        leftSection={<IconSearch size={14} />}
        placeholder="Почта, тариф или № счёта"
        aria-label="Поиск автопродления"
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
      />
      {query.isLoading ? (
        <LoadingState label="Загружаем..." />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<IconRepeat size={28} />}
          title={needle ? 'Ничего не нашлось' : 'Автопродлений пока нет'}
          description={needle ? undefined : 'Они появятся, когда покупатели отметят галочку автопродления при оплате.'}
        />
      ) : (
        <Paper withBorder radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
          <ScrollArea>
            <Table miw={760} verticalSpacing="sm" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Подключено</Table.Th>
                  <Table.Th>Покупатель</Table.Th>
                  <Table.Th>Условия</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th>Следующее списание</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => setOpenId(row.id)} data-testid={`subscription-${row.motherInvoiceId}`}>
                    <Table.Td><Text size="sm">{day(row.createdAt)}</Text></Table.Td>
                    <Table.Td><Text size="sm">{row.user?.email ?? 'аккаунт удалён'}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm">{row.title}</Text>
                      <Text size="xs" c="var(--text-muted)">{formatRubles(row.amountKopecks)} / {row.days} дн.</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Badge size="sm" variant="light" color={STATUS[row.status].color}>{STATUS[row.status].label}</Badge>
                        {row.test ? <Badge size="sm" variant="light" color="grape">{TEST_OFFER_BADGE}</Badge> : null}
                      </Group>
                    </Table.Td>
                    <Table.Td><Text size="sm">{nextText(row)}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}
      <Drawer opened={open !== null} onClose={() => setOpenId(null)} title="Автопродление" size="lg" position="right">
        {open ? <SubscriptionDetail row={open} /> : null}
      </Drawer>
    </>
  );
}

function SubscriptionDetail({ row }: { row: AdminSubscriptionRow }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const cancel = useMutation({
    mutationFn: () => adminCancelSubscription(row.id, { note }),
    onSuccess: (result) => {
      queryClient.setQueryData(ADMIN_SUBSCRIPTIONS_KEY, result);
      setNote('');
      notifications.show({ color: 'teal', message: 'Автопродление отключено, покупателю ушло подтверждение' });
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  return (
    <Stack gap="md" data-testid="subscription-detail">
      <div>
        <Title order={4}>{row.title}</Title>
        <Text size="sm" c="var(--text-muted)">
          {row.user ? `${row.user.displayName} — ${row.user.email}` : 'аккаунт удалён'} · счёт подключения № {row.motherInvoiceId}
        </Text>
      </div>
      <Group gap={4}>
        <Badge variant="light" color={STATUS[row.status].color}>{STATUS[row.status].label}</Badge>
        {row.test ? <Badge variant="light" color="grape">{TEST_OFFER_BADGE}</Badge> : null}
      </Group>
      <Text size="sm">
        {formatRubles(row.amountKopecks)} каждые {row.days} дн. ·{' '}
        {row.status === 'active' ? `следующее списание: ${nextText(row)}` : `отключено ${day(row.endedAt)} — причина в журнале`}
      </Text>
      {row.lastError ? (
        <Alert variant="light" color="orange" p="xs">
          <Text size="xs">Последняя ошибка: {row.lastError}</Text>
        </Alert>
      ) : null}
      {row.status === 'active' ? (
        <Paper withBorder p="sm" radius="md" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
          <Stack gap="xs">
            <Text fw={600} size="sm">Отключить по обращению</Text>
            <Text size="xs" c="var(--text-muted)">
              Списаний больше не будет, доступ останется до конца оплаченного срока. Покупатель получит письмо-подтверждение.
              Если нужен и возврат — оформите его в «Заказах».
            </Text>
            <Textarea size="xs" label="Причина" placeholder="Например: письмо в поддержку от 03.11" value={note} onChange={(event) => setNote(event.currentTarget.value)} minRows={2} />
            <Group>
              <Button size="xs" color="red" disabled={note.trim().length < 3} loading={cancel.isPending} onClick={() => cancel.mutate()}>
                Отключить автопродление
              </Button>
            </Group>
          </Stack>
        </Paper>
      ) : null}
      <Stack gap={6}>
        <Text size="sm" fw={600}>Журнал</Text>
        {row.events.map((event) => (
          <div key={event.id}>
            <Text size="xs" c="var(--text-muted)">{dateTime(event.at)}{event.actor ? ` · ${event.actor}` : ' · система'}</Text>
            <Text size="sm">{RECURRING_EVENT_LABELS[event.action] ?? event.action}{event.details ? ` — ${event.details}` : ''}</Text>
          </div>
        ))}
      </Stack>
    </Stack>
  );
}
